// Memory editor modal — extracted from Vault.tsx (kept verbatim).

import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { MicButton } from './MicButton.js';
import { LanguageSwitch } from './LanguageSwitch.js';
import { usePrefs, type Language } from '../lib/prefs.js';
import { TIERS } from '../lib/tiers.js';
import type { Memory } from '../lib/memoryModel.js';

function csv(s: string): string[] { return s.split(',').map((x) => x.trim()).filter(Boolean); }

interface OrgLite { id: string; name: string }

export function MemoryEditor({ memory, onClose, onSaved }: { memory: Memory; onClose: () => void; onSaved: () => void }) {
  const [prefs] = usePrefs();
  const [dictLang, setDictLang] = useState<Language>(prefs.language);
  const [draft, setDraft] = useState(memory);
  // Souvenir immuable: a reviewed memory is write-locked until the owner unlocks
  // it. Unlocking clears `reviewed` — editing the content invalidates the
  // "confirmed" state (and the server enforces the same rule).
  const [unlocked, setUnlocked] = useState(false);
  const locked = Boolean(memory.reviewed) && !unlocked;
  const unlock = () => { setUnlocked(true); setDraft((d) => ({ ...d, reviewed: false })); };
  const orgsQ = useQuery<{ organizations: OrgLite[] }>({ queryKey: ['organizations'], queryFn: () => api('/api/organizations') });
  const orgs = orgsQ.data?.organizations ?? [];
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const save = useMutation({
    mutationFn: () => api(`/api/memory/${memory.id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        title: draft.title, body: draft.body, approxYear: draft.approxYear,
        lifeChapter: draft.lifeChapter, themes: draft.themes, people: draft.people,
        places: draft.places, emotionalTone: draft.emotionalTone, visibility: draft.visibility,
        category: draft.category ?? 'personal', organizationId: draft.organizationId ?? null,
        reviewed: draft.reviewed ?? false,
      }),
    }),
    onSuccess: onSaved,
  });
  const del = useMutation({
    // Reviewed memories are write-locked server-side; the editor only enables
    // delete once unlocked, so we pass force to satisfy the guard.
    mutationFn: () => api(`/api/memory/${memory.id}?force=true`, { method: 'DELETE' }),
    onSuccess: onSaved,
  });
  const reenrich = useMutation({
    mutationFn: () => api(`/api/memory/${memory.id}/enrich`, { method: 'POST' }),
  });
  const [alignDone, setAlignDone] = useState(false);
  const align = useMutation({
    mutationFn: () => api('/api/analysis/from-memory', { method: 'POST', body: JSON.stringify({ memoryId: memory.id }) }),
    onSuccess: () => { setAlignDone(true); setDraft((d) => ({ ...d, reviewed: false })); },
  });
  const [fixMsg, setFixMsg] = useState<string | null>(null);
  const fixTypos = useMutation({
    mutationFn: () => api<{ ok: true; body: string; changed: boolean }>(`/api/memory/${memory.id}/fix-typos`, { method: 'POST' }),
    onMutate: () => setFixMsg(null),
    onSuccess: (r) => {
      if (r.changed) {
        setDraft((d) => ({ ...d, body: r.body }));
        setFixMsg('Typos corrected.');
      } else {
        setFixMsg('No typos found.');
      }
    },
    onError: () => setFixMsg('Engine in Claude Desktop mode — ask Claude Desktop: read_memory(id) → correct → apply_typo_fix.'),
  });

  return (
    <div className="fixed inset-0 z-[110] flex" onClick={onClose}>
      <div className="absolute inset-0 bg-black/45 backdrop-blur-[2px]" />
      {/* Right-side detail panel (slide-over) instead of a centered modal. */}
      <div onClick={(e) => e.stopPropagation()}
        className="relative ml-auto h-full w-[min(560px,94vw)] overflow-y-auto bg-void border-l border-ink/15 shadow-2xl p-6 space-y-4">
        <div className="flex items-center justify-between sticky -top-6 -mx-6 px-6 pt-6 pb-3 bg-void/95 backdrop-blur border-b border-ink/10 z-10">
          <h3 className="font-serif text-xl text-ink">Edit memory</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full border border-ink/15 grid place-items-center text-ink/50 hover:text-ink">✕</button>
        </div>

        {locked && (
          <div className="flex items-center gap-3 py-2.5 px-4 rounded-xl border border-emerald-300/30 bg-emerald-300/[0.05]">
            <span className="text-[13px] text-emerald-300/90 flex-1">🔒 Souvenir confirmé — <b>immuable</b>. Déverrouille-le pour le modifier ou le supprimer.</span>
            <button onClick={unlock}
              className="px-3 py-1.5 rounded-full border border-emerald-300/40 text-emerald-300 text-[11.5px] hover:bg-emerald-300/10 flex-shrink-0">
              Déverrouiller
            </button>
          </div>
        )}

        <div className={locked ? 'opacity-60 pointer-events-none select-none space-y-4' : 'space-y-4'}>
        <Field label="Title">
          <input value={draft.title ?? ''} onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            className="w-full px-3 py-2 rounded-lg border border-ink/15 bg-white/[0.02] font-serif text-[15px] text-ink focus:border-ink/40 focus:outline-none" />
        </Field>
        <Field label="Body">
          <div className="relative">
            <textarea value={draft.body} rows={6} onChange={(e) => setDraft({ ...draft, body: e.target.value })}
              className="w-full pl-3 pr-14 py-2 rounded-lg border border-ink/15 bg-white/[0.02] font-serif text-[15px] text-ink focus:border-ink/40 focus:outline-none resize-none" />
            <div className="absolute right-2 bottom-2 flex items-center gap-1.5">
              <LanguageSwitch value={dictLang} onChange={setDictLang} size="sm" />
              <MicButton size={32} language={dictLang} onAppend={(t) => setDraft((d) => ({ ...d, body: d.body ? d.body + ' ' + t : t }))} />
            </div>
          </div>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Year">
            <input type="number" value={draft.approxYear ?? ''} onChange={(e) => setDraft({ ...draft, approxYear: e.target.value ? Number(e.target.value) : null })}
              className="w-full px-3 py-2 rounded-lg border border-ink/15 bg-white/[0.02] font-mono text-[13px] text-ink focus:border-ink/40 focus:outline-none" />
          </Field>
          <Field label="Life chapter">
            <input value={draft.lifeChapter ?? ''} onChange={(e) => setDraft({ ...draft, lifeChapter: e.target.value || null })}
              className="w-full px-3 py-2 rounded-lg border border-ink/15 bg-white/[0.02] font-mono text-[13px] text-ink focus:border-ink/40 focus:outline-none" />
          </Field>
        </div>
        <Field label="People (comma-separated)">
          <input value={draft.people.join(', ')} onChange={(e) => setDraft({ ...draft, people: csv(e.target.value) })}
            className="w-full px-3 py-2 rounded-lg border border-ink/15 bg-white/[0.02] font-serif text-[14px] text-ink focus:border-ink/40 focus:outline-none" />
        </Field>
        <Field label="Themes (comma-separated)">
          <input value={draft.themes.join(', ')} onChange={(e) => setDraft({ ...draft, themes: csv(e.target.value) })}
            className="w-full px-3 py-2 rounded-lg border border-ink/15 bg-white/[0.02] font-serif text-[14px] text-ink focus:border-ink/40 focus:outline-none" />
        </Field>
        <Field label="Visibility">
          <div className="flex flex-wrap gap-1.5">
            {TIERS.map((t) => (
              <button key={t} onClick={() => setDraft({ ...draft, visibility: t })}
                className={`px-2.5 py-1 rounded-full text-[11.5px] border ${draft.visibility === t ? 'bg-amber text-void border-amber' : 'border-ink/15 text-ink/50 hover:text-ink'}`}>
                {t.replace('_', ' ')}
              </button>
            ))}
          </div>
        </Field>

        {/* Professional layer: category + organization scope */}
        <Field label="Category">
          <div className="flex items-center gap-2 flex-wrap">
            {(['personal', 'professional'] as const).map((c) => (
              <button key={c} onClick={() => setDraft({ ...draft, category: c, organizationId: c === 'personal' ? null : draft.organizationId })}
                className={`px-2.5 py-1 rounded-full text-[11.5px] border ${(draft.category ?? 'personal') === c ? 'bg-ink text-void border-ink' : 'border-ink/15 text-ink/50 hover:text-ink'}`}>
                {c}
              </button>
            ))}
            {(draft.category === 'professional') && orgs.length > 0 && (
              <select value={draft.organizationId ?? ''} onChange={(e) => setDraft({ ...draft, organizationId: e.target.value || null })}
                title="Scope to one organization (e.g. Pro-Siemens). Blank = all colleagues."
                className="px-2 py-1 rounded-lg border border-ink/15 bg-void text-[11.5px] text-ink/70">
                <option value="">all colleagues</option>
                {orgs.map((o) => <option key={o.id} value={o.id}>Pro-{o.name}</option>)}
              </select>
            )}
          </div>
        </Field>

        <Field label="Quality — 👍 reviewed / trusted · 👎 send to Align">
          <div className="flex items-center gap-2">
            <button onClick={() => setDraft({ ...draft, reviewed: !draft.reviewed })}
              title="Reviewed / trusted — reinforces the twin, disables alignment"
              className={`w-9 h-9 grid place-items-center rounded-full border text-[15px] ${draft.reviewed ? 'border-emerald-300/50 text-emerald-300 bg-emerald-300/[0.08]' : 'border-ink/15 text-ink/45 hover:text-emerald-300'}`}>👍</button>
            <button onClick={() => align.mutate()} disabled={align.isPending}
              title="Send to Align to confirm/correct"
              className="w-9 h-9 grid place-items-center rounded-full border border-ink/15 text-ink/45 text-[15px] hover:text-amber hover:border-amber/40 disabled:opacity-40">👎</button>
            {alignDone && <span className="text-[11px] text-amber/80">envoyé vers Align ✓</span>}
          </div>
        </Field>
        </div>

        <div className="flex items-center gap-3 pt-2 border-t border-ink/10">
          <button onClick={() => save.mutate()} disabled={save.isPending || locked}
            className="px-5 py-2 rounded-full bg-amber text-void text-[11.5px] hover:bg-particle disabled:opacity-40">
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
          <button onClick={() => reenrich.mutate()} disabled={reenrich.isPending || locked}
            className="px-4 py-2 rounded-full border border-ink/20 text-[11.5px] text-ink/60 hover:text-ink disabled:opacity-40">
            {reenrich.isPending ? 'Enriching…' : reenrich.isError ? 'Needs API key' : 'Re-enrich'}
          </button>
          <button onClick={() => fixTypos.mutate()} disabled={fixTypos.isPending || locked}
            className="px-4 py-2 rounded-full border border-ink/20 text-[11.5px] text-ink/60 hover:text-ink disabled:opacity-40">
            {fixTypos.isPending ? 'Fixing…' : 'Fix typos'}
          </button>
          {fixMsg && <span className="font-serif italic text-[12px] text-ink/55">{fixMsg}</span>}
          <button onClick={() => { if (confirm('Delete this memory permanently?')) del.mutate(); }} disabled={locked}
            className="ml-auto px-4 py-2 rounded-full border border-red-300/30 text-[11.5px] text-red-300/70 hover:text-red-300 disabled:opacity-40">
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-[11px] text-ink/45">{label}</span>
      {children}
    </label>
  );
}
